import { getMarketingParams, persistMarketingParams } from './marketingParams';
import { getLoginCallbackUrl, persistMcpOAuthParams } from './mcpOAuthParams';

const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || '';
const MICROSOFT_REDIRECT_URI = `${window.location.origin}/start`;
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || '';
const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI ||
  `${process.env.REACT_APP_AUTH_API_URL}/auth/apple/callback`;
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_ID || '';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function appendLoginHint(url, email) {
  if (!email) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}login_hint=${encodeURIComponent(email)}`;
}

function buildOAuthState(extra = {}) {
  persistMarketingParams();
  persistMcpOAuthParams();

  return encodeURIComponent(
    JSON.stringify({
      ...extra,
      ...getMarketingParams(),
    }),
  );
}

export function redirectGoogleLogin(email) {
  const scope = encodeURIComponent('openid profile email');
  const callbackUri = `${window.location.origin}/oauth/callback`;
  const redirectUri = encodeURIComponent(callbackUri);
  const state = buildOAuthState({ provider: 'google' });
  const nonce = Math.random().toString(36).substring(7);

  let authUrl =
    'https://accounts.google.com/o/oauth2/v2/auth' +
    `?client_id=${GOOGLE_CLIENT_ID}` +
    '&response_type=code' +
    `&redirect_uri=${redirectUri}` +
    `&scope=${scope}` +
    `&state=${state}` +
    `&nonce=${nonce}` +
    '&access_type=offline' +
    '&prompt=select_account';

  authUrl = appendLoginHint(authUrl, email);

  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'OPEN_EXTERNAL_BROWSER',
        data: { url: authUrl },
      }),
    );
    return;
  }

  try { sessionStorage.setItem('oauth_browser_flow', '1'); } catch (e) { /* ignore */ }
  window.location.href = authUrl;
}

export function redirectFacebookLogin() {
  const callbackUri = `${window.location.origin}/oauth/callback`;
  const redirectUri = encodeURIComponent(callbackUri);
  const scope = encodeURIComponent('public_profile,email');
  const state = buildOAuthState({ provider: 'facebook' });

  const authUrl =
    `https://www.facebook.com/v18.0/dialog/oauth` +
    `?client_id=${FACEBOOK_APP_ID}` +
    `&redirect_uri=${redirectUri}` +
    `&scope=${scope}` +
    `&state=${state}` +
    '&response_type=code';

  try { sessionStorage.setItem('oauth_browser_flow', '1'); } catch (e) { /* ignore */ }
  window.location.href = authUrl;
}

export function redirectMicrosoftLogin(email) {
  const scope = encodeURIComponent('openid profile email User.Read');
  const callbackUri = `${window.location.origin}/oauth/callback`;
  const redirectUri = encodeURIComponent(callbackUri);
  const state = buildOAuthState({ provider: 'microsoft' });
  const nonce = Math.random().toString(36).substring(7);

  let authUrl =
    'https://login.microsoftonline.com/common/oauth2/v2.0/authorize' +
    `?client_id=${MICROSOFT_CLIENT_ID}` +
    '&response_type=code' +
    `&redirect_uri=${redirectUri}` +
    `&scope=${scope}` +
    `&state=${state}` +
    `&nonce=${nonce}` +
    '&response_mode=query';

  authUrl = appendLoginHint(authUrl, email);

  try { sessionStorage.setItem('oauth_browser_flow', '1'); } catch (e) { /* ignore */ }
  window.location.href = authUrl;
}

export function openMicrosoftLoginPopup(onCompleted, email) {
  const scope = encodeURIComponent('openid profile email User.Read');
  const redirectUri = encodeURIComponent(MICROSOFT_REDIRECT_URI);
  const state = `microsoft_login_${Math.random().toString(36).substring(7)}`;
  const nonce = Math.random().toString(36).substring(7);

  let authUrl =
    'https://login.microsoftonline.com/common/oauth2/v2.0/authorize' +
    `?client_id=${MICROSOFT_CLIENT_ID}` +
    '&response_type=code' +
    `&redirect_uri=${redirectUri}` +
    `&scope=${scope}` +
    `&state=${state}` +
    `&nonce=${nonce}` +
    '&response_mode=query';

  authUrl = appendLoginHint(authUrl, email);

  const width = 500;
  const height = 600;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const popup = window.open(
    authUrl,
    'microsoft_login',
    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`,
  );

  const interval = setInterval(() => {
    try {
      if (!popup || popup.closed) {
        clearInterval(interval);
        return;
      }
      const { search } = popup.location;
      if (search && search.includes('code=')) {
        clearInterval(interval);
        popup.close();

        const params = new URLSearchParams(search.substring(1));
        onCompleted({
          code: params.get('code'),
          redirectUri: MICROSOFT_REDIRECT_URI,
        });
      } else if (search && search.includes('error=')) {
        clearInterval(interval);
        popup.close();

        const params = new URLSearchParams(search.substring(1));
        onCompleted({
          error: params.get('error_description') || 'Microsoft login failed',
        });
      }
    } catch (e) {
      // Cross-origin — popup still on Microsoft domain
    }
  }, 500);
}

export function redirectAppleLogin() {
  const nonce = Math.random().toString(36).substring(7);
  persistMarketingParams();
  persistMcpOAuthParams();
  const statePayload = window.ReactNativeWebView
    ? 'mobile'
    : JSON.stringify({
        callback_url: getLoginCallbackUrl(),
        login_page: window.location.origin,
        ...getMarketingParams(),
      });
  const state = encodeURIComponent(statePayload);
  const scope = encodeURIComponent('name email');
  const redirectUri = encodeURIComponent(APPLE_REDIRECT_URI);

  const authUrl =
    'https://appleid.apple.com/auth/authorize' +
    `?client_id=${APPLE_CLIENT_ID}` +
    `&redirect_uri=${redirectUri}` +
    '&response_type=code id_token' +
    `&scope=${scope}` +
    `&state=${state}` +
    `&nonce=${nonce}` +
    '&response_mode=form_post';

  window.location.href = authUrl;
}
